import React from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  DownloadSimple, AppleLogo, AndroidLogo, Desktop, DeviceMobile,
  CheckCircle, ShareNetwork, Browser, House
} from "@phosphor-icons/react";

const Step = ({ n, title, desc }) => (
  <div className="flex gap-4">
    <div className="w-8 h-8 shrink-0 rounded-full bg-[#E11D48]/20 border border-[#E11D48]/40 flex items-center justify-center font-display font-black text-sm text-[#E11D48]">{n}</div>
    <div>
      <div className="font-bold">{title}</div>
      <p className="text-zinc-400 text-sm mt-0.5 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const InstallApp = () => {
  const triggerInstall = () => {
    // Try to trigger native prompt if event is held
    const evt = window.__xookmedia_install_event;
    if (evt) {
      evt.prompt();
    } else {
      // Fallback: scroll to instructions
      document.getElementById("ios-instructions")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-2 gap-10 items-center mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#E11D48] font-bold mb-3">Application mobile & desktop</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-5">
              Téléchargez XooKmédia.
            </h1>
            <p className="text-zinc-300 text-lg leading-relaxed mb-6">
              Installez l'application en un clic sur votre téléphone, tablette ou ordinateur. Aucun store, aucune attente — c'est instantané, gratuit, et ça marche partout.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="install-cta" size="lg" onClick={triggerInstall} className="bg-[#E11D48] hover:bg-[#BE123C] text-white px-7">
                <DownloadSimple weight="bold" className="mr-2" /> Installer maintenant
              </Button>
            </div>
            <div className="mt-6 flex gap-6 text-sm text-zinc-400">
              <span className="flex items-center gap-1"><CheckCircle weight="fill" className="text-[#10B981]" /> Hors-ligne partiel</span>
              <span className="flex items-center gap-1"><CheckCircle weight="fill" className="text-[#10B981]" /> Notifications</span>
              <span className="flex items-center gap-1"><CheckCircle weight="fill" className="text-[#10B981]" /> Sans store</span>
            </div>
          </div>
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a0007] via-black to-[#0a0a0a]" />
            <div className="absolute inset-0 grain" />
            <div className="relative h-full flex items-center justify-center p-10">
              <div className="w-36 h-36 rounded-3xl bg-gradient-to-br from-[#E11D48] to-[#F59E0B] glow-primary flex items-center justify-center">
                <img src="/icon.svg" alt="XooKmédia" className="w-full h-full" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6">
            <DeviceMobile size={26} className="text-[#F59E0B] mb-3" weight="duotone" />
            <h3 className="font-display font-bold text-lg mb-3">Android & Chrome</h3>
            <div className="space-y-3 text-sm">
              <Step n="1" title="Ouvrez XooKmédia sur Chrome" desc="Sur votre téléphone Android." />
              <Step n="2" title="Menu (⋮)" desc="Touchez 'Installer l'application' ou 'Ajouter à l'écran d'accueil'." />
              <Step n="3" title="Validez" desc="L'icône apparaît sur votre écran d'accueil." />
            </div>
          </div>

          <div id="ios-instructions" className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6">
            <AppleLogo size={26} className="text-zinc-300 mb-3" weight="fill" />
            <h3 className="font-display font-bold text-lg mb-3">iPhone & iPad</h3>
            <div className="space-y-3 text-sm">
              <Step n="1" title="Ouvrez dans Safari" desc="L'installation n'est dispo que via Safari sur iOS." />
              <Step n="2" title="Touchez Partager" desc="L'icône carré avec une flèche vers le haut, en bas de l'écran." />
              <Step n="3" title="Sur l'écran d'accueil" desc="Faites défiler et choisissez « Sur l'écran d'accueil »." />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6">
            <Desktop size={26} className="text-[#10B981] mb-3" weight="duotone" />
            <h3 className="font-display font-bold text-lg mb-3">Windows / Mac / Linux</h3>
            <div className="space-y-3 text-sm">
              <Step n="1" title="Chrome ou Edge" desc="Ouvrez XooKmédia dans votre navigateur." />
              <Step n="2" title="Icône d'installation" desc="Une icône d'installation apparaît dans la barre d'adresse." />
              <Step n="3" title="Installer" desc="L'app devient un raccourci natif sur votre bureau." />
            </div>
          </div>
        </div>

        <div className="mt-12 p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a0007] to-[#0a0a0a]">
          <h3 className="font-display font-bold text-xl mb-2">App native iOS / Android ?</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            XooKmédia utilise la technologie <strong className="text-white">PWA (Progressive Web App)</strong> — la même que Twitter, Spotify Lite ou Pinterest. Pas besoin de l'App Store ni du Play Store : l'installation est instantanée, et l'app se met à jour automatiquement. Une version native publiée sur les stores arrivera dans une prochaine version.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InstallApp;
